import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Save, ExternalLink, Search, Target } from "lucide-react";
import { toast } from "sonner";

const MAX_COMPETITORS = 10;
const MAX_KEYPHRASES = 100;

/**
 * BUSeoEditor — inline panel that appears below a property card on the
 * Properties page. Lets an admin maintain up to 10 competitors (name + url)
 * and up to 100 keyphrases used for organic-search position tracking.
 *
 * Persists via PATCH /api/business-units/{id} — { competitors, keyphrases }.
 */
export default function BUSeoEditor({ businessUnit, onClose, onSaved }) {
  const buId = businessUnit?.business_unit_id;
  const [competitors, setCompetitors] = useState([]);
  const [keyphrasesText, setKeyphrasesText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCompetitors(
      (businessUnit?.competitors || []).map((c) => ({
        name: c.name || "",
        website_url: c.website_url || "",
      })),
    );
    setKeyphrasesText((businessUnit?.keyphrases || []).join("\n"));
  }, [buId]);

  // Derived: parsed keyphrases (de-duped, trimmed, capped to MAX)
  const parsedKeyphrases = useMemo(() => {
    const lines = (keyphrasesText || "").split(/\r?\n/);
    const seen = new Set();
    const out = [];
    for (const raw of lines) {
      const k = (raw || "").trim();
      if (!k) continue;
      const lower = k.toLowerCase();
      if (seen.has(lower)) continue;
      seen.add(lower);
      out.push(k);
      if (out.length >= MAX_KEYPHRASES) break;
    }
    return out;
  }, [keyphrasesText]);

  const totalRawLines = useMemo(
    () => (keyphrasesText || "").split(/\r?\n/).filter((l) => l.trim()).length,
    [keyphrasesText],
  );

  const addCompetitor = () => {
    if (competitors.length >= MAX_COMPETITORS) {
      toast.error(`Maximum ${MAX_COMPETITORS} competitors per property`);
      return;
    }
    setCompetitors([...competitors, { name: "", website_url: "" }]);
  };

  const updateCompetitor = (i, key, val) =>
    setCompetitors(competitors.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)));

  const removeCompetitor = (i) =>
    setCompetitors(competitors.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const cleanedCompetitors = competitors
        .map((c) => ({
          name: (c.name || "").trim(),
          website_url: (c.website_url || "").trim(),
        }))
        .filter((c) => c.name || c.website_url);
      if (cleanedCompetitors.length > MAX_COMPETITORS) {
        toast.error(`Trim to ${MAX_COMPETITORS} or fewer competitors`);
        return;
      }
      await api.patch(`/business-units/${buId}`, {
        competitors: cleanedCompetitors,
        keyphrases: parsedKeyphrases,
      });
      toast.success("Competitors & keyphrases saved");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="rounded-sm border-zinc-200 bg-zinc-50/40 ml-12"
      data-testid={`bu-seo-editor-${buId}`}>
      <CardContent className="p-5 space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="label-overline">SEO &amp; competition</div>
            <h3 className="font-display font-bold text-lg tracking-tight mt-1">
              {businessUnit?.name} — Competitors &amp; Keyphrases
            </h3>
            <p className="text-xs text-zinc-500 mt-1">
              Up to {MAX_COMPETITORS} competitors and {MAX_KEYPHRASES} keyphrases per property.
              Used in monthly reports and (Phase 2) live SERP position tracking.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="rounded-sm" onClick={onClose}
              data-testid={`bu-seo-editor-cancel-${buId}`}>
              Cancel
            </Button>
            <Button className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
              onClick={save} disabled={busy} data-testid={`bu-seo-editor-save-${buId}`}>
              <Save className="h-4 w-4 mr-1.5" />
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        {/* COMPETITORS */}
        <section data-testid={`bu-competitors-section-${buId}`}>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 grid place-items-center rounded-sm bg-[#002FA7]/10 text-[#002FA7]">
                <Target className="h-3.5 w-3.5" />
              </div>
              <div>
                <div className="label-overline">Competitors</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {competitors.length} of {MAX_COMPETITORS} added
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs"
              onClick={addCompetitor}
              disabled={competitors.length >= MAX_COMPETITORS}
              data-testid={`bu-competitor-add-${buId}`}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add competitor
            </Button>
          </div>

          {competitors.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-sm p-6 text-center
              text-xs text-zinc-500" data-testid={`bu-competitors-empty-${buId}`}>
              No competitors yet. Click <span className="font-mono">Add competitor</span> to start tracking.
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-[36px_1fr_1.4fr_40px] gap-2 px-2 label-overline text-zinc-500">
                <div>#</div>
                <div>Company name</div>
                <div>Website URL</div>
                <div />
              </div>
              {competitors.map((c, i) => (
                <div
                  key={`${buId}-competitor-${i}`}
                  className="grid grid-cols-[36px_1fr_1.4fr_40px] gap-2 items-center"
                  data-testid={`bu-competitor-row-${buId}-${i}`}
                >
                  <div className="text-[10px] font-mono text-zinc-400 text-center">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <Input
                    value={c.name}
                    onChange={(e) => updateCompetitor(i, "name", e.target.value)}
                    placeholder="e.g. Acme Lifts"
                    className="rounded-sm h-9"
                    data-testid={`bu-competitor-name-${buId}-${i}`}
                  />
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={c.website_url}
                      onChange={(e) => updateCompetitor(i, "website_url", e.target.value)}
                      placeholder="https://acmelifts.com"
                      className="rounded-sm h-9 font-mono text-xs"
                      data-testid={`bu-competitor-url-${buId}-${i}`}
                    />
                    {c.website_url && (
                      <a
                        href={c.website_url.startsWith("http") ? c.website_url : `https://${c.website_url}`}
                        target="_blank" rel="noreferrer"
                        title="Open in new tab"
                        className="text-zinc-400 hover:text-[#002FA7]"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                  <Button
                    variant="ghost" size="icon"
                    className="rounded-sm text-red-500 hover:text-red-700 h-9 w-9"
                    onClick={() => removeCompetitor(i)}
                    title="Remove competitor"
                    data-testid={`bu-competitor-remove-${buId}-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* KEYPHRASES */}
        <section data-testid={`bu-keyphrases-section-${buId}`}>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 grid place-items-center rounded-sm bg-emerald-50 text-emerald-700">
                <Search className="h-3.5 w-3.5" />
              </div>
              <div>
                <div className="label-overline">Keyphrases — SERP tracking</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {parsedKeyphrases.length} of {MAX_KEYPHRASES} unique
                  {totalRawLines > parsedKeyphrases.length && (
                    <span className="ml-1 text-amber-600 font-mono">
                      ({totalRawLines - parsedKeyphrases.length} duplicate{totalRawLines - parsedKeyphrases.length === 1 ? "" : "s"} ignored)
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Label className="text-[11px] uppercase tracking-wider text-zinc-500">
            One keyphrase per line — paste your full list below
          </Label>
          <Textarea
            value={keyphrasesText}
            onChange={(e) => setKeyphrasesText(e.target.value)}
            rows={Math.min(12, Math.max(6, totalRawLines + 1))}
            className="rounded-sm font-mono text-xs mt-1.5"
            placeholder={`elevator service brisbane\nlift maintenance brisbane\ncommercial lift installation\n...`}
            data-testid={`bu-keyphrases-textarea-${buId}`}
          />
          {parsedKeyphrases.length >= MAX_KEYPHRASES && (
            <p className="text-[11px] text-amber-700 mt-1.5">
              Capped at {MAX_KEYPHRASES}. Additional lines below the limit will be ignored when saving.
            </p>
          )}

          {parsedKeyphrases.length > 0 && (
            <div className="mt-3">
              <div className="label-overline mb-2">Preview ({parsedKeyphrases.length})</div>
              <div className="flex flex-wrap gap-1.5"
                data-testid={`bu-keyphrases-preview-${buId}`}>
                {parsedKeyphrases.slice(0, 60).map((k, i) => (
                  <span
                    key={`${k}-${i}`}
                    className="text-[11px] font-mono px-2 py-0.5 rounded-sm bg-white border border-zinc-200 text-zinc-700"
                  >
                    {k}
                  </span>
                ))}
                {parsedKeyphrases.length > 60 && (
                  <span className="text-[11px] font-mono px-2 py-0.5 rounded-sm bg-zinc-100 text-zinc-500">
                    +{parsedKeyphrases.length - 60} more…
                  </span>
                )}
              </div>
            </div>
          )}
        </section>
      </CardContent>
    </Card>
  );
}
